import React, { useEffect, useMemo, useState } from "react";
import {
  useAddDevicesToGroupMutation,
  useGetGroupDevicesQuery,
  useRemoveDevicesFromGroupMutation,
} from "../../redux/admin/groupApi";
import { useGetLiteDevicesPageQuery } from "../../redux/admin/adminApi";
import { toast } from "react-hot-toast";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Divider,
  Skeleton,
  useMediaQuery,
  Pagination,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

import {
  X,
  Search,
  Smartphone,
  Wifi,
  WifiOff,
  Check,
  CheckCircle2,
} from "lucide-react";

const AddDeviceToGroupModal = ({ groupId, initialGroupDevices = [], isOpen, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const PAGE_SIZE = 50;

  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [removedDeviceIds, setRemovedDeviceIds] = useState([]);
  const [addingDeviceIds, setAddingDeviceIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);

  // Reset when closed
  useEffect(() => {
    if (!isOpen) {
      setSelectedDeviceIds([]);
      setRemovedDeviceIds([]);
      setAddingDeviceIds([]);
      setSearchQuery("");
      setPage(1);
    }
  }, [isOpen]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  const { data: devicesPage, isLoading: isDevicesLoading, isFetching: isDevicesFetching } =
    useGetLiteDevicesPageQuery(
      {
        page,
        limit: PAGE_SIZE,
        ...(searchQuery.trim() ? { search: searchQuery.trim() } : {}),
      },
      { skip: !isOpen }
    );

  const { data: currentGroupDevices, isFetching: isGroupDevicesFetching } =
    useGetGroupDevicesQuery(
      { groupId },
      { skip: !isOpen || !groupId, refetchOnMountOrArgChange: true }
    );

  const [addDevicesToGroup, { isLoading: isAdding }] =
    useAddDevicesToGroupMutation();
  const [removeDevicesFromGroup, { isLoading: isRemoving }] =
    useRemoveDevicesFromGroupMutation();

  const devices = useMemo(() => {
    const deviceList = devicesPage?.data ?? devicesPage?.data?.data ?? [];
    return Array.isArray(deviceList) ? deviceList : [];
  }, [devicesPage]);

  const totalDevices = useMemo(
    () => Number(devicesPage?.total || devicesPage?.data?.total || devices.length || 0),
    [devicesPage, devices.length]
  );

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalDevices / PAGE_SIZE)),
    [totalDevices]
  );

  const currentGroupDeviceRows = useMemo(() => {
    const apiDevices =
      currentGroupDevices?.data?.devices ?? currentGroupDevices?.devices ?? null;
    if (Array.isArray(apiDevices) && apiDevices.length > 0) {
      return apiDevices;
    }
    return Array.isArray(initialGroupDevices) ? initialGroupDevices : [];
  }, [currentGroupDevices, initialGroupDevices]);

  const devicesInGroup = useMemo(() => {
    if (!Array.isArray(currentGroupDeviceRows)) return new Set();
    return new Set(
      currentGroupDeviceRows
        .map((device) => String(device?._id || ""))
        .filter(Boolean)
    );
  }, [currentGroupDeviceRows]);

  const filteredDevices = useMemo(() => {
    const merged = new Map();
    currentGroupDeviceRows.forEach((device) => {
      const id = String(device?._id || "");
      if (id) merged.set(id, device);
    });
    devices.forEach((device) => {
      const id = String(device?._id || "");
      if (id && !merged.has(id)) merged.set(id, device);
    });
    const mergedRows = Array.from(merged.values());
    const query = String(searchQuery || "").trim().toLowerCase();
    if (!query) return mergedRows;
    return mergedRows.filter((device) => {
      const name = String(device?.name || "").toLowerCase();
      const imei = String(device?.imei || "").toLowerCase();
      const serial = String(device?.serialNumber || "").toLowerCase();
      const vehicleNumber = String(device?.vehicleNumber || "").toLowerCase();
      const model = String(device?.model || "").toLowerCase();
      return (
        name.includes(query) ||
        imei.includes(query) ||
        serial.includes(query) ||
        vehicleNumber.includes(query) ||
        model.includes(query)
      );
    });
  }, [currentGroupDeviceRows, devices, searchQuery]);

  const isInGroup = (deviceId) => devicesInGroup.has(deviceId);
  const isEffectivelyInGroup = (deviceId) =>
    (isInGroup(deviceId) && !removedDeviceIds.includes(deviceId)) ||
    selectedDeviceIds.includes(deviceId);

  const sortedDevices = useMemo(() => {
    const sortByName = (left, right) =>
      String(left?.name || left?.vehicleNumber || left?.imei || "").localeCompare(
        String(right?.name || right?.vehicleNumber || right?.imei || ""),
        undefined,
        { sensitivity: "base" }
      );

    const assigned = [];
    const unassigned = [];

    filteredDevices.forEach((device) => {
      if (isEffectivelyInGroup(device?._id)) {
        assigned.push(device);
      } else {
        unassigned.push(device);
      }
    });

    assigned.sort(sortByName);
    unassigned.sort(sortByName);

    return [...assigned, ...unassigned];
  }, [addingDeviceIds, filteredDevices, selectedDeviceIds, removedDeviceIds, devicesInGroup]);

  const availableDevices = useMemo(
    () => filteredDevices.filter((d) => !isEffectivelyInGroup(d._id)),
    [filteredDevices, devicesInGroup, selectedDeviceIds, removedDeviceIds]
  );

  const availableCount = availableDevices.length;

  const handleToggleDevice = async (deviceId) => {
    if (!groupId) {
      toast.error("Group not found");
      return;
    }
    if (addingDeviceIds.includes(deviceId)) {
      return;
    }

    const currentlyInGroup = isEffectivelyInGroup(deviceId);

    setAddingDeviceIds((prev) => [...prev, deviceId]);

    if (currentlyInGroup) {
      setSelectedDeviceIds((prev) => prev.filter((id) => id !== deviceId));
      setRemovedDeviceIds((prev) =>
        prev.includes(deviceId) ? prev : [...prev, deviceId]
      );
    } else {
      setSelectedDeviceIds((prev) =>
        prev.includes(deviceId) ? prev : [...prev, deviceId]
      );
      setRemovedDeviceIds((prev) => prev.filter((id) => id !== deviceId));
    }

    try {
      if (currentlyInGroup) {
        await removeDevicesFromGroup({
          groupId,
          deviceIds: [deviceId],
        }).unwrap();
        toast.success("Device removed from group");
      } else {
        await addDevicesToGroup({
          groupId,
          deviceIds: [deviceId],
        }).unwrap();
        toast.success("Device added to group");
      }
    } catch (error) {
      if (currentlyInGroup) {
        setRemovedDeviceIds((prev) => prev.filter((id) => id !== deviceId));
        setSelectedDeviceIds((prev) =>
          prev.includes(deviceId) ? prev : [...prev, deviceId]
        );
      } else {
        setSelectedDeviceIds((prev) => prev.filter((id) => id !== deviceId));
        setRemovedDeviceIds((prev) => prev.filter((id) => id !== deviceId));
      }
      console.error(error);
      toast.error(error?.data?.msg || "Failed to update group device");
    } finally {
      setAddingDeviceIds((prev) => prev.filter((id) => id !== deviceId));
    }
  };

  return (
    <Dialog
      open={Boolean(isOpen)}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          overflow: "hidden",
        },
      }}
    >
      {/* ✅ HEADER */}
      <DialogTitle
        sx={{
          p: 2,
          background: alpha(theme.palette.primary.main, 0.06),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack spacing={0.2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2.2,
                  display: "grid",
                  placeItems: "center",
                  background: theme.palette.primary.main,
                  color: "white",
                }}
              >
                <Smartphone size={18} />
              </Box>
              <Typography variant="h6" fontWeight={900}>
                Add Devices to Group
              </Typography>
              {(isGroupDevicesFetching || isDevicesLoading || isDevicesFetching) && (
                <Chip
                  size="small"
                  label="Loading…"
                  variant="outlined"
                  sx={{ borderRadius: 2, fontWeight: 800 }}
                />
              )}
            </Stack>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Tick/untick turant reflect hoga. Backend process background me chalta rahega, aur in-group devices top par dikhte hain.
            </Typography>
          </Stack>

          <Tooltip title="Close">
            <IconButton onClick={onClose} sx={{ borderRadius: 2 }}>
              <X size={18} />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>

      {/* ✅ SEARCH + ACTIONS (Sticky) */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
          position: "sticky",
          top: 0,
          zIndex: 2,
          background: theme.palette.background.paper,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.2}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <TextField
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, IMEI, or serial…"
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
              maxWidth: { sm: 520 },
            }}
          />

        </Stack>
      </Box>

      {/* ✅ CONTENT */}
      <DialogContent
        sx={{
          p: 2,
          background: alpha(theme.palette.background.default, 0.4),
        }}
      >
        {/* Top info row */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          sx={{ mb: 1.5 }}
        >
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              label={`Available: ${availableCount}`}
              variant="outlined"
              sx={{ borderRadius: 2, fontWeight: 800 }}
            />
            <Chip
              label={`In Group: ${sortedDevices.filter((device) => isEffectivelyInGroup(device._id)).length}`}
              color={sortedDevices.some((device) => isEffectivelyInGroup(device._id)) ? "primary" : "default"}
              variant={sortedDevices.some((device) => isEffectivelyInGroup(device._id)) ? "filled" : "outlined"}
              sx={{ borderRadius: 2, fontWeight: 900 }}
            />
          </Stack>

          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Tip: Search aur pagination se devices turant load honge.
          </Typography>
        </Stack>

        {/* List container */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
            overflow: "hidden",
          }}
        >
          {/* Loading */}
          {isDevicesLoading ? (
            <Box sx={{ p: 2 }}>
              <Stack spacing={1.2}>
                {Array.from({ length: 7 }).map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      p: 1.4,
                      borderRadius: 2.5,
                      border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                      background: theme.palette.background.paper,
                    }}
                  >
                    <Stack direction="row" spacing={1.4} alignItems="center">
                      <Skeleton variant="rounded" width={46} height={46} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton width="45%" />
                        <Skeleton width="65%" />
                      </Box>
                      <Skeleton width={36} height={36} variant="rounded" />
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Box>
          ) : sortedDevices.length > 0 ? (
            <Box
              sx={{
                maxHeight: fullScreen ? "calc(100vh - 260px)" : 440,
                overflow: "auto",
              }}
            >
              {sortedDevices.map((device, idx) => {
                const alreadyInGroup = isEffectivelyInGroup(device._id);
                const isSelected = alreadyInGroup;
                const isAddingThisDevice = addingDeviceIds.includes(device._id);
                const isOnline = device.status === "online";

                return (
                  <Box key={device._id}>
                    <Box
                      onClick={() => handleToggleDevice(device._id)}
                      sx={{
                        px: 1.5,
                        py: 1.2,
                        cursor: isAddingThisDevice ? "default" : "pointer",
                        background: alreadyInGroup
                          ? alpha(theme.palette.success.main, 0.06)
                          : isSelected
                          ? alpha(theme.palette.primary.main, 0.08)
                          : theme.palette.background.paper,
                        opacity: alreadyInGroup ? 0.75 : 1,
                        transition: "0.12s ease",
                        "&:hover": {
                          background: isAddingThisDevice
                            ? alreadyInGroup
                              ? alpha(theme.palette.success.main, 0.06)
                              : theme.palette.background.paper
                            : alreadyInGroup
                              ? alpha(theme.palette.success.main, 0.12)
                              : alpha(theme.palette.primary.main, 0.06),
                        },
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.2}
                        alignItems={{ xs: "flex-start", sm: "center" }}
                      >
                        {/* Status icon */}
                        <Box
                          sx={{
                            width: 46,
                            height: 46,
                            borderRadius: 2.5,
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                            background: alreadyInGroup
                              ? alpha(theme.palette.success.main, 0.14)
                              : isOnline
                              ? alpha(theme.palette.success.main, 0.14)
                              : alpha(theme.palette.text.secondary, 0.12),
                            color: alreadyInGroup
                              ? theme.palette.success.main
                              : isOnline
                              ? theme.palette.success.main
                              : theme.palette.text.secondary,
                          }}
                        >
                          {alreadyInGroup ? (
                            <CheckCircle2 size={22} />
                          ) : isOnline ? (
                            <Wifi size={22} />
                          ) : (
                            <WifiOff size={22} />
                          )}
                        </Box>

                        {/* Info */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                          >
                            <Typography fontWeight={900} noWrap>
                              {device.name || "Unnamed Device"}
                            </Typography>

                            <Chip
                              size="small"
                              label={isOnline ? "Online" : "Offline"}
                              color={isOnline ? "success" : "default"}
                              variant={isOnline ? "filled" : "outlined"}
                              sx={{ borderRadius: 2, fontWeight: 800 }}
                            />

                            {alreadyInGroup && (
                              <Chip
                                size="small"
                                label="In this group"
                                color="success"
                                variant="outlined"
                                sx={{ borderRadius: 2, fontWeight: 800 }}
                              />
                            )}
                          </Stack>

                          <Typography
                            variant="body2"
                            sx={{ color: "text.secondary" }}
                            noWrap
                          >
                            IMEI: {device.imei || "—"}
                            {device.model ? ` • ${device.model}` : ""}
                            {device.serialNumber
                              ? ` • SN: ${device.serialNumber}`
                              : ""}
                          </Typography>
                        </Box>

                        {/* Checkbox area */}
                        {!alreadyInGroup ? (
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: 2,
                              display: "grid",
                              placeItems: "center",
                              flexShrink: 0,
                              border: `1px solid ${alpha(
                                theme.palette.divider,
                                0.9
                              )}`,
                              background: isSelected
                                ? theme.palette.primary.main
                                : theme.palette.background.paper,
                              color: isSelected ? "white" : "transparent",
                              transition: "0.12s ease",
                            }}
                          >
                            <Check size={18} />
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: 2,
                              display: "grid",
                              placeItems: "center",
                              flexShrink: 0,
                              background: theme.palette.success.main,
                              color: "white",
                            }}
                          >
                            <Check size={18} />
                          </Box>
                        )}
                      </Stack>
                    </Box>

                    {idx !== sortedDevices.length - 1 && <Divider />}
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Box
              sx={{
                p: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                textAlign: "center",
              }}
            >
              <Box
                sx={{
                  width: 70,
                  height: 70,
                  borderRadius: 3,
                  display: "grid",
                  placeItems: "center",
                  background: alpha(theme.palette.primary.main, 0.08),
                }}
              >
                <Smartphone size={32} />
              </Box>
              <Typography variant="h6" fontWeight={900}>
                No devices found
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {searchQuery
                  ? "Try a different search term."
                  : "No devices available to add."}
              </Typography>
            </Box>
          )}
        </Paper>
      </DialogContent>

      {/* ✅ FOOTER */}
      <DialogActions
        sx={{
          p: 2,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
          background: theme.palette.background.paper,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ width: "100%" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {`Showing ${devices.length} of ${totalDevices} devices`}
          </Typography>

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Pagination
              page={Math.min(page, totalPages)}
              count={totalPages}
              onChange={(_, value) => setPage(value)}
              color="primary"
              shape="rounded"
              size={fullScreen ? "small" : "medium"}
              disabled={isDevicesLoading || isDevicesFetching}
            />
            <Button
              onClick={onClose}
              variant="contained"
              sx={{ borderRadius: 2, fontWeight: 800 }}
            >
              Okay
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default AddDeviceToGroupModal;
